import type { AddressLookupRow, TrafficTopDimension, TrafficTopRow } from '@bartizo/shared';
import { countryName } from './country.js';
import { trafficRisk, type TrafficRisk } from './traffic-risk.js';

/**
 * The filters on the Traffic page's explorer and "worth a look" list (P11.15).
 *
 * Plain functions over the rows the API already returned, so a filter changes what
 * is shown and asks for nothing: the lists are the capped top lists the agents
 * report, and a filter can only narrow them, never reach past them.
 */

export const RISK_RANK: Readonly<Record<TrafficRisk, number>> = {
  none: 0,
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

export type BotCheck = 'any' | 'verified' | 'fake' | 'unchecked';

export interface TrafficFilters {
  /** Text in the name, or (for an address) its network or country. */
  q: string;
  /** Nothing below this risk. */
  minRisk: TrafficRisk;
  minRequests: number;
  /** At least a fifth of its answers were refusals or failures. */
  onlyRefused: boolean;
  /** An address's country, as `GB`; empty for any. */
  country: string;
  /** Addresses only: leave out those every server already stops. */
  notStopped: boolean;
  /** Bots only. */
  botCheck: BotCheck;
}

export const NO_FILTERS: TrafficFilters = {
  q: '',
  minRisk: 'none',
  minRequests: 0,
  onlyRefused: false,
  country: '',
  notStopped: false,
  botCheck: 'any',
};

/** Which filters mean something on which list; the others are not offered. */
export const FILTERS_FOR: Readonly<
  Record<TrafficTopDimension, readonly ('risk' | 'refused' | 'country' | 'stopped' | 'botCheck')[]>
> = {
  host: ['risk', 'refused'],
  path: ['risk', 'refused'],
  ip: ['risk', 'refused', 'country', 'stopped'],
  bot: ['risk', 'refused', 'botCheck'],
  ua_family: ['refused'],
  status: [],
  category: [],
  site_category: [],
  site_bot: [],
  site_ip: [],
  site_path: [],
  site_status: [],
};

const refusedShare = (row: TrafficTopRow): number =>
  row.requests === 0 ? 0 : (row.status4xx + row.status5xx) / row.requests;

const isFake = (row: TrafficTopRow): boolean =>
  row.key.startsWith('fake:') || row.verification === 'failed';

export function matchesFilters(
  row: TrafficTopRow,
  dimension: TrafficTopDimension,
  filters: TrafficFilters,
  statusOf: ReadonlyMap<string, AddressLookupRow> = new Map(),
): boolean {
  const offered = FILTERS_FOR[dimension];
  const q = filters.q.trim().toLowerCase();
  if (q !== '') {
    const haystack = [
      row.key,
      row.geo?.network ?? '',
      row.geo?.country === null || row.geo === undefined ? '' : countryName(row.geo.country),
      row.geo?.country ?? '',
    ]
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  if (row.requests < filters.minRequests) return false;
  if (offered.includes('risk') && RISK_RANK[trafficRisk(row).risk] < RISK_RANK[filters.minRisk]) {
    return false;
  }
  if (offered.includes('refused') && filters.onlyRefused && refusedShare(row) < 0.2) return false;
  if (
    offered.includes('country') &&
    filters.country !== '' &&
    row.geo?.country !== filters.country
  ) {
    return false;
  }
  if (offered.includes('stopped') && filters.notStopped) {
    const status = statusOf.get(row.key)?.status;
    // Not looked up yet is not "stopped": it stays until it is known.
    if (status === 'stopped' || status === 'protected') return false;
  }
  if (offered.includes('botCheck') && filters.botCheck !== 'any') {
    const fake = isFake(row);
    const verified = !fake && row.verification === 'verified';
    if (filters.botCheck === 'fake' && !fake) return false;
    if (filters.botCheck === 'verified' && !verified) return false;
    if (filters.botCheck === 'unchecked' && (fake || verified)) return false;
  }
  return true;
}

export const applyFilters = (
  rows: readonly TrafficTopRow[],
  dimension: TrafficTopDimension,
  filters: TrafficFilters,
  statusOf?: ReadonlyMap<string, AddressLookupRow>,
): TrafficTopRow[] => rows.filter((row) => matchesFilters(row, dimension, filters, statusOf));

/** How many filters are doing something, for a "Clear (n)" button. */
export function activeFilters(filters: TrafficFilters): number {
  return [
    filters.q.trim() !== '',
    filters.minRisk !== 'none',
    filters.minRequests > 0,
    filters.onlyRefused,
    filters.country !== '',
    filters.notStopped,
    filters.botCheck !== 'any',
  ].filter(Boolean).length;
}

/** The countries in a list of address rows, busiest first, for the country filter. */
export function countriesIn(rows: readonly TrafficTopRow[]): string[] {
  const requests = new Map<string, number>();
  for (const row of rows) {
    const code = row.geo?.country;
    if (code !== null && code !== undefined) {
      requests.set(code, (requests.get(code) ?? 0) + row.requests);
    }
  }
  return [...requests.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([code]) => code);
}

/** What "worth a look" gathers from, and what each is called. */
export type SuspectType = 'ip' | 'path' | 'bot' | 'host';
export const SUSPECT_LABELS: Readonly<Record<SuspectType, string>> = {
  ip: 'Address',
  path: 'Page',
  bot: 'Bot',
  host: 'Site',
};

export interface Suspect {
  type: SuspectType;
  row: TrafficTopRow;
  risk: TrafficRisk;
  /** In words: what made it worth a look. */
  reason: string;
}

const count = (value: number): string => Math.round(value).toLocaleString('en-US');

/** The lists' rows turned into what is worth a look: risk first, then how much it asked. */
export function suspectsOf(
  lists: Readonly<Record<SuspectType, readonly TrafficTopRow[]>>,
  minRisk: TrafficRisk = 'medium',
): Suspect[] {
  const found: Suspect[] = [];
  for (const type of Object.keys(lists) as SuspectType[]) {
    for (const row of lists[type]) {
      const reading = trafficRisk(row);
      if (reading.risk === 'none' || RISK_RANK[reading.risk] < RISK_RANK[minRisk]) continue;
      const parts = [`${reading.why}, ${count(row.requests)} requests`];
      if (row.geo?.country !== null && row.geo?.country !== undefined) {
        parts.push(`from ${countryName(row.geo.country)}`);
      }
      if (row.geo?.network != null) parts.push(row.geo.network);
      found.push({ type, row, risk: reading.risk, reason: parts.join(' · ') });
    }
  }
  return found.sort(
    (a, b) =>
      RISK_RANK[b.risk] - RISK_RANK[a.risk] ||
      refusedShare(b.row) * b.row.requests - refusedShare(a.row) * a.row.requests ||
      a.row.key.localeCompare(b.row.key),
  );
}
